import { useEffect, useMemo, useState } from 'react';

import { ReactComponent as ArrowSVG } from '@assets/SVGs/PresenteeismTable/ArrowSVG.svg';
import { ReactComponent as ColaboradorFichajesSVG } from '@assets/SVGs/QontactPoint/Turnos/Colaboradores.svg';
import Logo from '@assets/qontact-logo-orange.png';
import styles from '@styles/components/Colaborador/ColaboradorFichaje.module.css';

import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Application } from '@core/Application';
import { PresenteeismTableRequest } from '@services/UsersService';

const months = [
	'Enero',
	'Febrero',
	'Marzo',
	'Abril',
	'Mayo',
	'Junio',
	'Julio',
	'Agosto',
	'Septiembre',
	'Octubre',
	'Noviembre',
	'Diciembre',
]; // TODO: localize this

export class PresenteeismUserSummary {
    orgName: string
    total: number = 0
    onTime: number = 0
    late: number = 0
    withoutShift: number = 0
    records: PresenteeismUserRecordSummary[] = []
    averageCheckInHour: string = 'N/A'
    averageCheckOutHour: string = 'N/A'
    totalHoursWorked: string = 'N/A'
    totalExtraHoursWorked: string = 'N/A'
}

interface PresenteeismUserRecordSummary {
    buildingName: string
    date: string
    checkInHour: string
    checkOutHour: string
    totalHoursWorked: string // HH:MM:ss format
    extraHoursWorked: string // HH:MM:ss format
    timestamp: number 
}

export default function ColaboradorFichaje({ orgID, colaborador, hideTitle = false }) {
	const [loading, setLoading] = useState(false);

	const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1); // Default to current month
	const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

	const [orgName, setOrgName] = useState('');
	const [total, setTotal] = useState(0);
	const [onTime, setOnTime] = useState(0);
	const [late, setLate] = useState(0);
	const [withoutShift, setWithoutShift] = useState(0);
	const [records, setRecords] = useState<PresenteeismUserRecordSummary[]>([]);
	const [averageCheckInHour, setAverageCheckInHour] = useState('N/A');
	const [averageCheckOutHour, setAverageCheckOutHour] = useState('N/A');
	const [totalHoursWorked, setTotalHoursWorked] = useState('N/A');
	const [totalExtraHoursWorked, setTotalExtraHoursWorked] = useState('N/A');

	const [lateWidth, withoutShiftWidth, onTimeWidth] = useMemo(() => {
		if (total === 0) return [0, 0, 0];

		return [(late / total) * 100, (withoutShift / total) * 100, (onTime / total) * 100];
	}, [late, withoutShift, onTime, total]);

	useEffect(() => {
		console.log('[Colaborador] ' + colaborador);
		fetchReporteList();
	}, [orgID, selectedMonth]);

	const fetchReporteList = async () => {
		const yearMonth = `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}`;

		setLoading(true);
		try {
			const usersService = Application.shared().coreDependencies.usersService;
			const phone = colaborador.mobilePhone
			
			const tableRequest: PresenteeismTableRequest = {
				yearMonth: yearMonth
			};
			
			const json = await usersService.getWhatsappPresenteeismTable(phone, orgID, tableRequest);
			console.log(json);
			let result = json as PresenteeismUserSummary;

			setOrgName(result.orgName);
			setTotal(result.total);
			setOnTime(result.onTime);
			setLate(result.late);
			setWithoutShift(result.withoutShift);
			setRecords(result.records.sort((a, b) => a.timestamp - b.timestamp));
			setAverageCheckInHour(result.averageCheckInHour);
			setAverageCheckOutHour(result.averageCheckOutHour);
			setTotalHoursWorked(result.totalHoursWorked);
			setTotalExtraHoursWorked(result.totalExtraHoursWorked);
		} catch (error) {
			console.error(error);
		}
		setLoading(false);
	};

	const handleIncrementMonth = () => {
		let newMonth;
		if (selectedMonth === 12) {
			setSelectedYear(prevYear => prevYear + 1);
			newMonth = 1;
		} else {
			newMonth = selectedMonth + 1;
		}
		setSelectedMonth(newMonth);
	};

	
	const handleDecrementMonth = () => {
		let newMonth;
		if (selectedMonth === 1) {
			setSelectedYear(prevYear => prevYear - 1);
			newMonth = 12;
		} else {
			newMonth = selectedMonth - 1;
		}
		setSelectedMonth(newMonth);
	};

	const handlePDFDownload = async () => {
		setLoading(true);
		const doc = new jsPDF();

		try {
			doc.setFontSize(12);
			doc.text(`Planilla de control horario ${orgName}`, 15, 20);
			doc.text(`Colaborador: ${colaborador.name}`, 15, 30);

			const logoUrl = Logo;

			const pageWidth = doc.internal.pageSize.width;
			const pageHeight = doc.internal.pageSize.height;

			const logoMaxWidth = pageWidth * 0.2;

			const logoAspectRatio = 483/90; // Original ratio from your dimensions (5.36:1)
			const logoWidth = logoMaxWidth;
			const logoHeight = logoWidth / logoAspectRatio;

			const xPositionImg = pageWidth - logoWidth - 15;
			const yPositionImg = 20;

			doc.addImage(
				logoUrl,
				'PNG',
				xPositionImg,
				yPositionImg,
				logoWidth,
				logoHeight
			);


			let yPosition = 35;
			doc.setFontSize(14);
			yPosition += 10;
			if (!records || records.length === 0) {
				doc.text('Sin ingresos para este mes.', 15, yPosition);
			} else {
				autoTable(doc, {
					startY: yPosition,
					head: [
						[
							'Colaborador',
							'Edificio',
							'Fecha',
							'Ingreso',
							'Egreso',
							'Horas trabajadas',
							'Horas extra',
						],
					],
					body: records.map((record) => [
						colaborador.name,
						record.buildingName,
						record.date,
						record.checkInHour,
						record.checkOutHour,
						record.totalHoursWorked,
						record.extraHoursWorked,
					]),
					theme: 'grid',
					styles: { fontSize: 8 },
					headStyles: { fillColor: [41, 128, 185], textColor: 255 },
				});

				const summaryData = [
					{ label: "Hora promedio de ingreso", value: averageCheckInHour },
					{ label: "Hora promedio de salida", value: averageCheckOutHour },
					{ label: "Total de horas trabajadas", value: totalHoursWorked },
					{ label: "Total horas extras", value: totalExtraHoursWorked },
				];
				
				autoTable(doc, {
					startY: (doc as any).lastAutoTable.finalY + 20,
					body: summaryData.map(item => [`${item.label}: ${item.value}`]),
					theme: 'grid',
					styles: {
						fontSize: 8,
						cellPadding: 2,
						textColor: [40, 40, 40]
					},
					columnStyles: {
						0: { cellWidth: 100, fontStyle: 'bold', fillColor: [41, 128, 185], textColor: 255 },
					},
					tableWidth: 'wrap',
					showHead: 'never',
					margin: { left: 15 }
				});
				
				// Add signature section
				const finalY = (doc as any).lastAutoTable.finalY + 20;
				doc.setFontSize(10);
				doc.text("Firma del empleado: ________________________", 15, finalY);
				doc.text("Fecha: ________________________", 15, finalY + 15);
			}

			doc.save(`reporte_${colaborador.name}_${`${selectedYear}-${selectedMonth.toString().padStart(2, '0')}`}.pdf`);
		} catch (error) {
			alert('Error generando PDF. Comunicate con soporte.');
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className={styles['content-container']}>
			<div className={styles['nomina-header']} style={{ marginBottom: '46px' }}>
				{!hideTitle ? (
					<div className={styles['colaborador-header']}>
						<div
							className={styles['svg-container']}
							style={{ marginRight: '15px', marginLeft: '15px', marginTop: '-5px' }}>
							<ColaboradorFichajesSVG />
						</div>
						<h2 className={styles['title-nomina']}>Fichajes</h2>
					</div>
				) : null}

				<div className={styles['month-changer-container']}>
				{months[selectedMonth - 1]} {selectedYear}
					<div className={styles['month-buttons']}>
						<div
							className={styles['arrow-svg-container']}
							style={{ transform: 'rotate(90deg)' }}
							onClick={handleDecrementMonth}>
							<ArrowSVG />
						</div>

						<div
							className={styles['arrow-svg-container']}
							style={{ transform: 'rotate(-90deg)' }}
							onClick={handleIncrementMonth}>
							<ArrowSVG />
						</div>
					</div>
				</div>
			</div>

			<div className={styles['fichajes-container']}>
				<div className={styles['total-box']}>
					<p>Total Registros</p>
					<p>{total}</p>
				</div>

				<div className={styles['bars-container']}>
					<div>
						<div className={styles['bar-title']}>
							<p>{onTime}</p>
							<p>a tiempo</p>
						</div>
						<div className={styles['bar']}>
							<div style={{ width: `${onTimeWidth}%` }} />
						</div>
					</div>

					<div>
						<div className={styles['bar-title']}>
							<p>{late}</p>
							<p>entrada tarde o salida temprana</p>
						</div>
						<div className={styles['bar']}>
							<div style={{ width: `${lateWidth}%` }} />
						</div>
					</div>

					<div>
						<div className={styles['bar-title']}>
							<p>{withoutShift}</p>
							<p>sin turno</p>
						</div>
						<div className={styles['bar']}>
							<div style={{ width: `${withoutShiftWidth}%` }} />
						</div>
					</div>
				</div>
			</div>

            <div className={styles['pdf-download']} onClick={handlePDFDownload}>    
                <div className={styles['svg-container']} style={{ marginRight: '5px', marginLeft: '10px' }}>
                    <svg xmlns="http://www.w3.org/2000/svg" width="100%" height="100%" viewBox="0 0 16 18" fill="none">
                        <path d="M15.9333 12.258C15.8883 12.1489 15.8251 12.0485 15.7466 11.961L13.0792 9.261C12.9117 9.09153 12.6847 8.99632 12.4479 8.99632C12.2111 8.99632 11.984 9.09153 11.8166 9.261C11.6492 9.43047 11.5551 9.66033 11.5551 9.9C11.5551 10.1397 11.6492 10.3695 11.8166 10.539L12.9725 11.7H8.00222C7.7664 11.7 7.54025 11.7948 7.3735 11.9636C7.20676 12.1324 7.11308 12.3613 7.11308 12.6C7.11308 12.8387 7.20676 13.0676 7.3735 13.2364C7.54025 13.4052 7.7664 13.5 8.00222 13.5H12.9725L11.8166 14.661C11.7333 14.7447 11.6671 14.8442 11.622 14.9539C11.5768 15.0636 11.5536 15.1812 11.5536 15.3C11.5536 15.4188 11.5768 15.5364 11.622 15.6461C11.6671 15.7558 11.7333 15.8553 11.8166 15.939C11.8993 16.0234 11.9976 16.0903 12.106 16.136C12.2143 16.1817 12.3305 16.2052 12.4479 16.2052C12.5653 16.2052 12.6815 16.1817 12.7898 16.136C12.8982 16.0903 12.9965 16.0234 13.0792 15.939L15.7466 13.239C15.8289 13.1545 15.8926 13.0533 15.9333 12.942C16.0222 12.7229 16.0222 12.4771 15.9333 12.258ZM9.78049 16.2H2.66741C2.43159 16.2 2.20544 16.1052 2.03869 15.9364C1.87195 15.7676 1.77827 15.5387 1.77827 15.3V2.7C1.77827 2.46131 1.87195 2.23239 2.03869 2.0636C2.20544 1.89482 2.43159 1.8 2.66741 1.8H7.11308V4.5C7.11308 5.21608 7.39411 5.90284 7.89435 6.40919C8.39458 6.91554 9.07305 7.2 9.78049 7.2H13.337C13.5126 7.19911 13.6839 7.14565 13.8295 7.04635C13.9751 6.94705 14.0883 6.80636 14.155 6.642C14.2231 6.4781 14.2417 6.29746 14.2085 6.12287C14.1753 5.94828 14.0917 5.78756 13.9683 5.661L8.6335 0.261C8.56 0.190995 8.47597 0.133273 8.38455 0.0899999H8.30452L8.05556 0H2.66741C1.95997 0 1.2815 0.284464 0.781265 0.790812C0.281029 1.29716 0 1.98392 0 2.7V15.3C0 16.0161 0.281029 16.7028 0.781265 17.2092C1.2815 17.7155 1.95997 18 2.66741 18H9.78049C10.0163 18 10.2425 17.9052 10.4092 17.7364C10.5759 17.5676 10.6696 17.3387 10.6696 17.1C10.6696 16.8613 10.5759 16.6324 10.4092 16.4636C10.2425 16.2948 10.0163 16.2 9.78049 16.2ZM8.89135 3.069L11.1942 5.4H9.78049C9.54467 5.4 9.31852 5.30518 9.15177 5.1364C8.98503 4.96761 8.89135 4.73869 8.89135 4.5V3.069Z" fill="#EC6C56"/>
                    </svg>
                </div>
                Exportar datos
            </div>
		</div>
	);
}
